<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-green" sprite="megaphone" />
          Paging Groups
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-green" sprite="megaphone" />
            Paging Groups
          </h1>
        </el-col>

        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ total }}
          </el-tag>
        </el-col>
      </el-row>

      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable/>
          <el-button
            class="ml-10 hidden-xs-only"
            @click="doExport"
          >
            Export
          </el-button>
        </div>
        <div class="box right">
          <el-button @click="openCreateModal">Add New</el-button>
        </div>
      </div>

      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{enabled: true, gt: 50, oSize: 0}"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>

      <create-form
        :createVisible="createVisible"
        :createProcessing="createProcessing"
        :numInUse="numInUse"
        :extOptions="extOptions"
        :clearFormFields="clearFormFields"
        :reRenderKey="reRenderKey"
        @remoteMethodForExts="remoteMethodForExts"
        @checkForAvailableNum="checkForAvailableNum"
        @cancelOperation="cancelOperation"
        @createOperation="createOperation"
      />

      <edit-form
        :editVisible="editVisible"
        :reRenderKey="reRenderKey"
        :editProcessing="editProcessing"
        :numInUse="numInUse"
        :extOptions="extOptions"
        :clearFormFields="clearFormFields"
        :selectedPagingGroup="selectedPagingGroup"
        @remoteMethodForExts="remoteMethodForExts"
        @checkForAvailableNum="checkForAvailableNum"
        @cancelOperation="cancelOperation"
        @editOperation="editOperation"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import CreateForm from "./components/createForm";
import EditForm from "./components/editForm";
import { debounce } from "lodash";
import feathersClient from "../../../feathers/feathers-client";
import {globalEventBus} from "@/globalEventBus";
import {ExportToCsv} from "export-to-csv";

export default {
  name: "PagingGroupsTable",
  data() {
    return {
      total: 0,
      pagingGroupsChannel: null,
      extOptions: [],
      numInUse: null,
      selectedPagingGroup: null,
      tableData: [],
      allGroups: [],
      search: "",
      visibleActionsId: "",
      tableLoading: false,
      editVisible: false,
      editProcessing: false,
      createVisible: false,
      createProcessing: false,
      availableNumQuery: null,
      remoteMethodExtsQuery: null,
      clearFormFields: false,
      reRenderKey: "",
      tableColumns: [
        {
          field: 'sprite',
          title: '',
          width: 50,
          slots: {
            default: ({row}) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-green" sprite="megaphone"/>
                </div>
              ]
            }
          }
        },
        {
          field: 'name',
          title: 'Name',
          minWidth: 150,
          showOverflow: true,
          sortable: true
        },
        {
          field: 'number',
          title: 'Number',
          minWidth: 100,
          showOverflow: false,
          sortable: true
        },
        {
          field: 'actions',
          title: '',
          minWidth: '150',
          showOverflow: false,
          slots: {
            default: ({row}) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={'edit-'+ row.id}
                    icon="el-icon-edit"
                    onClick={() => this.openEditModal(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={'delete-'+row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteOperation(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>
              ]
            }
          }
        }
      ]
    };
  },

  components: {
    CreateForm,
    EditForm,
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
  },

  watch: {
    selectedTenant() {
      this.findPagingGroups();
      this.getExtensionOptions();
      feathersClient.service('channel-memberships').remove(null, {query: {channelName: this.pagingGroupsChannel}});
      this.joinSocketChannel();
    },

    user() {
      this.findPagingGroups();
      this.getExtensionOptions();
      if(!this.pagingGroupsChannel){
        this.joinSocketChannel();
      }
    },

    availableNumQuery: debounce(function () {
      feathersClient.service('check-number')
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            number: this.availableNumQuery,
          },
        })
        .then((res) => {
          this.numInUse = res;
        });
    }, 500),

    remoteMethodExtsQuery: debounce(function () {
      feathersClient
        .service("extensions")
        .find({
          query: {
            tenantId: this.selectedTenant.id,
            $or: [
              { number: { $like: `%${this.remoteMethodExtsQuery}%` } },
              { name: { $like: `%${this.remoteMethodExtsQuery}%` } },
            ],
            $limit: 25
          },
        })
        .then((res) => {
          this.extOptions = [...res];
        });
    }, 500),

    search: debounce(function () {
      this.searchGroups();
    }, 500)
  },

  methods: {
    ...mapActions("paging-groups", {
      deletePagingGroups: "remove",
      patchPagingGroups: "patch",
      createPagingGroups: "create",
    }),

    doExport(){
      const data = [...this.tableData];
      if(data.length){

        const writeData = data.map(item => ({
          name: item.name,
          number: item.number ? item.number : ''
        }));

        const options = {
          filename: 'paging_groups',
          showLabels: true,
          headers: ['Name', 'Number']
        }

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    searchGroups() {
      if(this.search.length){
        this.tableData = this.tableData.filter(
          (data) =>
            data.number.toLowerCase().includes(this.search.toLowerCase()) ||
            data.name.toLowerCase().includes(this.search.toLowerCase())
        );
      } else{
        this.tableData = [...this.allGroups];
      }
      this.total = this.tableData.length;
    },

    generateRandomKey() {
      const generateRandomString = () => Math.random().toString(36).slice(2);
      return `${generateRandomString()}-${generateRandomString()}$${generateRandomString()}`;
    },

    changeReRenderKey() {
      this.reRenderKey = this.generateRandomKey();
    },

    openCreateModal() {
      this.createVisible = true;
      this.clearFormFields = false;
    },

    openEditModal(id) {
      feathersClient
        .service("paging-groups")
        .get(id)
        .then((res) => (this.selectedPagingGroup = res));
      this.clearFormFields = false;
      this.editVisible = true;
    },

    remoteMethodForExts(query) {
      this.remoteMethodExtsQuery = query;
    },
    //General Form and Table Operations
    cellClicked(data){
      if(data.column.property !== 'actions'){
        this.openEditModal(data.row.id);
      }
    },

    rowHover(data){
      this.visibleActionsId = data.row.id;
    },

    rowLeave(){
      this.visibleActionsId = "";
    },

    findPagingGroups(){
      this.tableLoading = true;
      this.tableData = [];
      feathersClient.service('paging-groups').find({
        query: {
          tenantId: this.selectedTenant.id
        }
      }).then( res => {
        this.tableData = [...res];
        this.allGroups = [...res];
        this.total = res.length;
        this.tableLoading = false;
      }).catch( () => {
        this.tableLoading = false;
      })
    },

    // ⭐️ Num In Use Query ⭐️ //
    checkForAvailableNum(query) {
      this.availableNumQuery = query;
    },

    // ⭐️ Create Operation ⭐️ //
    createOperation(createBody) {
      this.$confirm("Process paging group create?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.createProcessing = true;
          this.numInUse = null;

          this.createPagingGroups(createBody)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Created successfully!",
                type: "success",
              });
              this.changeReRenderKey();
              this.createProcessing = false;
              this.cancelOperation("create");
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }
              this.createProcessing = false;
              this.clearFormFields = false;
            });
        })
        .catch(() => true);
    },

    // ⭐️ Edit Operation ⭐️ //
    editOperation(editBody) {
      this.$confirm("Process paging group edit?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.editProcessing = true;

          this.patchPagingGroups([this.selectedPagingGroup.id, editBody])
            .then(() => {
              this.changeReRenderKey();
              this.$notify({
                title: "Success",
                message: "Edited successfully!",
                type: "success",
              });
              this.numInUse = null;
              this.editProcessing = false;
              this.cancelOperation("edit");
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }
              this.editProcessing = false;
              this.clearFormFields = false;
            });
        })
        .catch(() => true);
    },

    // ⭐️ Delete Operation ⭐️ //
    deleteOperation(id) {
      this.$confirm("Process paging group delete?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          this.tableLoading = true;
          this.deletePagingGroups(id)
            .then(() => {
              this.$notify({
                title: "Success",
                message: "Deleted successfully!",
                type: "success",
              });
              this.tableLoading = false;
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  title: error.message,
                  message: error.errors[Object.keys(error.errors)[0]],
                  type: "error",
                });
              } else {
                this.$notify({
                  title: error.name,
                  message: error.message,
                  type: "error",
                });
              }
              this.tableLoading = false;
            });
        })
        .catch(() => true);
    },

    // ⭐️ Cancel Operation ⭐️ //
    cancelOperation(type) {
      if (type === "create") {
        this.createVisible = false;
        this.clearFormFields = true;
      } else if (type === "edit") {
        this.editVisible = false;
        this.clearFormFields = true;
      }
      this.changeReRenderKey();
      this.numInUse = null;
    },

    getExtensionOptions() {
      feathersClient.service('extensions').find({
        paginate: false,
        query: {
          tenantId: this.selectedTenant.id,
          $limit: 25
        },
      }).then((res) => {
        this.extOptions = [...res];
      }).catch( () => {
        this.extOptions = [];
      });
    },

    joinSocketChannel(){
      const channelService = feathersClient.service('channel-memberships');
      const data = {
        roomType: 'admins',
        tenantId: this.selectedTenant.id
      };
      channelService.create(data).then(res => {this.pagingGroupsChannel = `${data.roomType}/${data.tenantId}`});
    },

    removeListeners(){
      feathersClient.service('paging-groups').removeListener('patched');
      feathersClient.service('paging-groups').removeListener('created');
      feathersClient.service('paging-groups').removeListener('removed');
      globalEventBus.$off('appReconnected');
    }
  },
  mounted() {
    if (this.user) {
      this.findPagingGroups();
      this.joinSocketChannel();
    }
  },

  created() {
    this.changeReRenderKey();
    this.getExtensionOptions();

    //event listeners
    //monitor socket connection and reconnect to channel
    globalEventBus.$on('appReconnected', () => {
      this.joinSocketChannel();
    });

    //record was edited
    //always refresh did destination options
    feathersClient.service('paging-groups').on('patched', (data) => {
      const updateIndex = this.tableData.findIndex(item => item.id === data.id);
      const updateAllIndex = this.allGroups.findIndex(item => item.id === data.id);
      if(updateIndex >= 0){this.tableData.splice(updateIndex, 1, data);}
      if(updateAllIndex >= 0){ this.allGroups.splice(updateAllIndex, 1, data);}
    });

    //record was created
    feathersClient.service('paging-groups').on('created', (data) => {
      this.tableData.push(data);
      this.allGroups.push(data);
    });

    //record was deleted
    feathersClient.service('paging-groups').on('removed', (data) => {
      const indexToRemove = this.tableData.findIndex(item => item.id === data.id);
      const indexAllToRemove = this.allGroups.findIndex(item => item.id === data.id);
      if(indexToRemove >= 0){this.tableData.splice(indexToRemove, 1);}
      if(indexAllToRemove >= 0){this.allGroups.splice(indexAllToRemove, 1);}
    });
  },

  beforeDestroy() {
    this.removeListeners();
    feathersClient.service('channel-memberships').remove(null, {query: {channelName: this.pagingGroupsChannel}});
  }
};
</script>

